import { Avatar, Box, Card, CardContent, Container, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import type { StaffMember, InfrastructureImage } from '../../shared/types';
import { SCHOOL } from '../../shared/constants';

interface AboutData {
  principalMessage: { name: string; photoUrl: string; message: string };
  staff: StaffMember[];
  infrastructure: InfrastructureImage[];
}

export default function AboutPage() {
  const { data } = useQuery({
    queryKey: ['public-about'],
    queryFn: async () => (await api.get<AboutData>('/public/about')).data,
  });
  const [tab, setTab] = useState<'ALL' | 'LAB' | 'CLASSROOM' | 'LIBRARY' | 'SPORTS' | 'OTHER'>('ALL');
  const filteredImages = (data?.infrastructure ?? []).filter((i) => tab === 'ALL' || i.category === tab);

  return (
    <Box>
      <Box sx={{ bgcolor: 'primary.dark', color: '#fff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 2 }}>About us</Typography>
          <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: 36, md: 56 } }}>
            A school where every<br />student is seen, heard, valued.
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, opacity: 0.85, fontWeight: 400, maxWidth: 720 }}>
            For nearly four decades, {SCHOOL.name} has shaped young minds through a balanced focus on academics, arts, sports and character.
          </Typography>
        </Container>
      </Box>

      {/* Principal's message */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>From the Principal's Desk</Typography>
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                <Avatar src={data?.principalMessage.photoUrl} sx={{ width: 140, height: 140, mx: 'auto' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2 }}>{data?.principalMessage.name}</Typography>
                <Typography variant="caption" color="text.secondary">Principal</Typography>
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.8 }}>
                  "{data?.principalMessage.message}"
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      {/* Staff */}
      <Box sx={{ bgcolor: '#F1F5F9', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Faculty & Staff</Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>Meet the dedicated educators behind Baseras.</Typography>
          <Grid container spacing={3}>
            {(data?.staff ?? []).map((s) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={s.id}>
                <Card sx={{ textAlign: 'center', height: '100%' }}>
                  <CardContent>
                    <Avatar src={s.photoUrl} sx={{ width: 96, height: 96, mx: 'auto', mb: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{s.name}</Typography>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 700, display: 'block' }}>
                      {s.designation}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{s.department}</Typography>
                    {s.bio && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>{s.bio}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Infrastructure */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Infrastructure</Typography>
            <Typography color="text.secondary">Modern facilities to support holistic learning.</Typography>
          </Box>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
            <Tab value="ALL" label="All" />
            <Tab value="LAB" label="Labs" />
            <Tab value="CLASSROOM" label="Classrooms" />
            <Tab value="LIBRARY" label="Library" />
            <Tab value="SPORTS" label="Sports" />
            <Tab value="OTHER" label="Other" />
          </Tabs>
        </Stack>
        <Grid container spacing={2}>
          {filteredImages.map((img) => (
            <Grid item xs={12} sm={6} md={4} key={img.id}>
              <Card>
                <Box component="img" src={img.imageUrl} alt={img.caption} sx={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }} />
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{img.caption}</Typography>
                  <Typography variant="caption" color="text.secondary">{img.category}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
